import { envConfig } from "@/config";
import { posthog } from "posthog-js";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

export const initPostHog = (username: string, email: string, applianceId: string, applianceName: string): void => {
	if (!posthog.__loaded) {
		posthog.init(envConfig.POSTHOG_KEY || "", {
			api_host: envConfig.POSTHOG_HOST || "https://app.posthog.com",
			autocapture: true, // Enable automatic event tracking
			capture_pageview: false, // Set to false to manually track page views
			capture_performance: true, // Enable performance tracking
			disable_session_recording: false, // Enable session recording
			session_recording: {
				recordCrossOriginIframes: true,
				maskAllInputs: false,
				maskInputOptions: {
					password: true,
				},
			},
			session_idle_timeout_seconds: 28800, // Session timeout set to 8 hrs
		});
	}

	if (username && applianceId) {
		const postHogUserIdentifier = username + " + " + applianceId;
		posthog.identify(postHogUserIdentifier, {
			username,
			email,
			applianceId,
			applianceName,
		});
	}
};

export const usePosthogRouteChangeListener = (): void => {
	const location = useLocation();

	useEffect(() => {
		// Capture pageview event on route change
		posthog.capture("$pageview", { url: location.pathname });
	}, [location]);
};

export const resetPosthog = (): void => {
	posthog.reset();
};

/**
 * Captures a focus event via PostHog.
 *
 * @param isPreset - If true, captures a 'focus_preset' event; otherwise, captures a 'focus_applied' event.
 * @param isLocked - Whether the focus is locked (tabsLocked).
 * @param teacherId - The logged in teacher's unique identifier.
 * @param applianceId - The appliance/device unique identifier.
 */
export function captureFocusEvent(isPreset: boolean, isLocked: boolean, teacherId: string, applianceId: string): void {
	//capture it as preset event
	if (isPreset) {
		posthog.capture("focus_preset", {
			teacherId: teacherId,
			applianceId: applianceId,
			isLocked: isLocked,
		});
	} else {
		posthog.capture("focus_applied", {
			teacherId: teacherId,
			applianceId: applianceId,
			isLocked: isLocked,
		});
	}
}

/**
 * Converts TimeInput24Hr object to readable time string format.
 *
 * @param time - The time object with hour and minute properties.
 * @returns Formatted time string in HH:MM format.
 */
function formatTimeInput24Hr(time: { hour: number; minute: number }): string {
	return `${time.hour.toString().padStart(2, "0")}:${time.minute.toString().padStart(2, "0")}`;
}

/**
 * Captures a schedule event (create or update) via PostHog.
 *
 * @param isUpdate - If true, captures a 'schedule_updated' event; otherwise, captures a 'schedule_created' event.
 * @param day - The day of the week for the schedule.
 * @param startTime - The start time object of the schedule.
 * @param endTime - The end time object of the schedule.
 * @param endDate - The end date of the schedule.
 * @param active - Whether the schedule is active.
 * @param teacherUsername - The logged in teacher's username.
 * @param applianceId - The appliance/device unique identifier.
 * @param classroomName - The classroom display name.
 */
export function captureScheduleCreationOrUpdateEvent(
	isUpdate: boolean,
	day: string,
	startTime: { hour: number; minute: number },
	endTime: { hour: number; minute: number },
	endDate: Date,
	active: boolean,
	teacherUsername: string,
	applianceId: string,
	classroomName: string
): void {
	const eventName = isUpdate ? "class_schedule_updated" : "class_schedule_created";

	posthog.capture(eventName, {
		teacherId: teacherUsername,
		applianceId: applianceId,
		classroomName: classroomName,
		day: day,
		startTime: formatTimeInput24Hr(startTime),
		endTime: formatTimeInput24Hr(endTime),
		endDate: endDate.toISOString(),
		active: active,
	});
}

/**
 * Captures a schedule deletion event via PostHog.
 *
 * @param day - The day of the week for the deleted schedule.
 * @param startTime - The start time object of the deleted schedule.
 * @param endTime - The end time object of the deleted schedule.
 * @param endDate - The end date of the deleted schedule.
 * @param active - Whether the deleted schedule was active.
 * @param teacherUsername - The logged in teacher's username.
 * @param applianceId - The appliance/device unique identifier.
 * @param classroomName - The classroom display name.
 */
export function captureScheduleDeletionEvent(
	day: string,
	startTime: { hour: number; minute: number },
	endTime: { hour: number; minute: number },
	endDate: Date,
	active: boolean,
	teacherUsername: string,
	applianceId: string,
	classroomName: string
): void {
	posthog.capture("class_schedule_deleted", {
		teacherId: teacherUsername,
		applianceId: applianceId,
		classroomName: classroomName,
		day: day,
		startTime: formatTimeInput24Hr(startTime),
		endTime: formatTimeInput24Hr(endTime),
		endDate: endDate.toISOString(),
		active: active,
	});
}

/**
 * Captures a schedule duplicate event via PostHog.
 *
 * @param teacherUsername - The logged in teacher's username.
 * @param applianceId - The appliance/device unique identifier.
 * @param classroomName - The classroom display name.
 */
export function captureScheduleDuplicateEvent(teacherUsername: string, applianceId: string, classroomName: string): void {
	posthog.capture("class_schedule_duplicated", {
		teacherId: teacherUsername,
		applianceId: applianceId,
		classroomName: classroomName,
	});
}

/**
 * Captures a schedule toggle (activate/deactivate) event via PostHog.
 *
 * @param isActive - Whether the schedule is being activated (true) or deactivated (false).
 * @param day - The day of the week for the schedule.
 * @param startTime - The start time object of the schedule.
 * @param endTime - The end time object of the schedule.
 * @param endDate - The end date of the schedule.
 * @param teacherUsername - The logged in teacher's username.
 * @param applianceId - The appliance/device unique identifier.
 * @param classroomName - The classroom display name.
 */
export function captureScheduleToggleEvent(
	isActive: boolean,
	day: string,
	startTime: { hour: number; minute: number },
	endTime: { hour: number; minute: number },
	endDate: Date,
	teacherUsername: string,
	applianceId: string,
	classroomName: string
): void {
	const eventName = isActive ? "class_schedule_activated" : "class_schedule_deactivated";

	posthog.capture(eventName, {
		teacherId: teacherUsername,
		applianceId: applianceId,
		classroomName: classroomName,
		day: day,
		startTime: formatTimeInput24Hr(startTime),
		endTime: formatTimeInput24Hr(endTime),
		endDate: endDate.toISOString(),
		active: isActive,
	});
}
